import styled from '@emotion/styled';
import { css } from '@emotion/react';
import React from 'react';

import { Button } from '../Button';
import { Status } from '../Status';

import type { ConnectedAppCardProps, ConnectedAppCardColorMode, ConnectedAppStatus } from './ConnectedAppCard.types';

/**
 * Generates a light tint of a color (10% opacity)
 */
const getLightTint = (hexColor: string): string => {
  return `${hexColor}14`; // 14 in hex = ~8% opacity
};

/**
 * Get status badge configuration based on connection status
 */
const getStatusConfig = (status: ConnectedAppStatus, customText?: string) => {
  switch (status) {
    case 'connected':
      return {
        type: 'success' as const,
        label: customText || 'Connected',
      };
    case 'available':
      return {
        type: 'neutral' as const,
        label: customText || 'Not configured',
      };
    case 'addon':
      return {
        type: 'info' as const,
        label: customText || 'Request Access',
      };
    case 'comingSoon':
      return {
        type: 'neutral' as const,
        label: customText || 'Coming Soon',
      };
    default:
      return {
        type: 'neutral' as const,
        label: customText || 'Unknown',
      };
  }
};

interface ContainerStyleProps {
  $cardColorMode: ConnectedAppCardColorMode;
  $appColor: string;
  $dimmed: boolean;
}

/**
 * Main container for the card
 */
const Container = styled.div<ContainerStyleProps>`
  ${({ theme, $cardColorMode, $appColor, $dimmed }) => {
    const backgroundColor = $cardColorMode === 'app' ? getLightTint($appColor) : theme.colors.white;

    return css`
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 16px;
      background: ${backgroundColor};
      border: 1px solid ${theme.colors.border.light};
      border-radius: 8px;
      transition: background-color 0.2s ease, opacity 0.2s ease;
      opacity: ${$dimmed ? 0.6 : 1};
      cursor: ${$dimmed ? 'not-allowed' : 'default'};
    `;
  }}
`;

interface LogoStyleProps {
  $grayscale: boolean;
}

/**
 * Logo container
 */
const Logo = styled.img<LogoStyleProps>`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: contain;
  flex-shrink: 0;
  transition: filter 0.2s ease;
  filter: ${({ $grayscale }) => ($grayscale ? 'grayscale(50%)' : 'none')};
`;

/**
 * Info section containing name and status
 */
const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

interface NameStyleProps {
  $titleColorMode: ConnectedAppCardColorMode;
  $appColor: string;
}

/**
 * App name/title
 */
const Name = styled.h3<NameStyleProps>`
  ${({ theme, $titleColorMode, $appColor }) => {
    const textColor = $titleColorMode === 'app' ? $appColor : theme.colors.text.primary;

    return css`
      font-size: 16px;
      font-weight: 600;
      font-family: ${theme.typography.fontFamily};
      color: ${textColor};
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `;
  }}
`;

/**
 * Right section for actions
 */
const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

interface BadgeStyleProps {
  $dimmed: boolean;
}

/**
 * Static badge for non-clickable actions (e.g., Coming Soon)
 */
const StaticBadge = styled.span<BadgeStyleProps>`
  ${({ theme, $dimmed }) => css`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    font-family: ${theme.typography.fontFamily};
    border-radius: 12px;
    white-space: nowrap;
    border: none;
    color: ${$dimmed ? theme.colors.text.muted : theme.colors.text.primary};
    background: ${$dimmed ? '#F5F5F5' : '#E8E8E8'};
    cursor: ${$dimmed ? 'not-allowed' : 'default'};
  `}
`;

export const ConnectedAppCard: React.FC<ConnectedAppCardProps> = ({
  name,
  logoPath,
  color,
  cardColorMode = 'standard',
  titleColorMode = 'app',
  status = 'available',
  primaryAction,
  rightSection,
  showStatus,
  statusText,
  dimmed,
  grayscaleLogo,
  logoAlt,
  id,
  dataId,
  dataTestId,
  className,
  style,
}) => {
  const statusConfig = getStatusConfig(status, statusText);

  // Determine dimmed state: explicit prop > default based on status
  const isDimmed = dimmed ?? status === 'comingSoon';
  
  // Determine grayscale state: explicit prop > default based on status
  const isGrayscale = grayscaleLogo ?? status === 'comingSoon';
  
  // Determine if status badge should be shown: explicit prop > default based on status
  const shouldShowStatus = showStatus ?? (status !== 'comingSoon' && status !== 'addon');

  // Determine action rendering: button if onClick provided, static badge otherwise
  const renderAction = () => {
    if (!primaryAction) return null;

    const { label, onClick, useAppColor, disabled, size = 'xs' } = primaryAction;

    // If no onClick, render as static badge
    if (!onClick) {
      return <StaticBadge $dimmed={isDimmed}>{label}</StaticBadge>;
    }

    // Determine button styling based on useAppColor prop
    const shouldUseAppColor = useAppColor ?? (status === 'connected' || status === 'available');

    if (shouldUseAppColor) {
      // Solid button with app color background
      return (
        <Button
          variant="primary"
          backgroundColor={color}
          textColor="#FFFFFF"
          size={size}
          onClick={onClick}
          disabled={disabled}
        >
          {label}
        </Button>
      );
    } else {
      // Outline button with app color
      return (
        <Button
          variant="outlineSecondary"
          outlineColor={color}
          size={size}
          onClick={onClick}
          disabled={disabled}
        >
          {label}
        </Button>
      );
    }
  };

  return (
    <Container
      $cardColorMode={cardColorMode}
      $appColor={color}
      $dimmed={isDimmed}
      id={id}
      data-id={dataId}
      data-testid={dataTestId}
      className={className}
      style={style}
    >
      <Logo src={logoPath} alt={logoAlt || name} $grayscale={isGrayscale} />
      <Info>
        <Name $titleColorMode={titleColorMode} $appColor={color}>
          {name}
        </Name>
        {shouldShowStatus && (
          <Status
            type={statusConfig.type}
            label={statusConfig.label}
            size="small"
            variant="filled"
            showIcon={false}
          />
        )}
      </Info>
      {(primaryAction || rightSection) && (
        <RightSection>
          {rightSection}
          {renderAction()}
        </RightSection>
      )}
    </Container>
  );
};

ConnectedAppCard.displayName = 'ConnectedAppCard';

export default ConnectedAppCard;
